import React, { useCallback, useEffect, useRef, useState } from "react";
import { UserResponse } from "../types/user";
import {
  getCurrentUser,
  login,
  logout,
  refreshAccessToken,
} from "../services/authService";
import { clearDeviceMosquitoCache } from "../services/deviceMosquitoService";
import { clearHistoryCache } from "../services/historyService";
import { clearStatMosquitoCache } from "../services/statMosquitoService";
import { AuthContext, AuthContextType } from "./authTypes";
import { AUTH_REDIRECT_REASON_STORAGE_KEY } from "../utils/axiosConfig";

type RefreshUserStatus = "success" | "session-invalid" | "stale" | "error";

const IDLE_TIMEOUT_MS = 120 * 60 * 1000;
const TOKEN_REFRESH_THROTTLE_MS = 5 * 60 * 1000;
const USER_ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

interface RefreshUserResult {
  user: UserResponse | null;
  status: RefreshUserStatus;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [authStatus, setAuthStatus] =
    useState<AuthContextType["authStatus"]>("checking");
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(false);
  const refreshRequestIdRef = useRef(0);
  const userRef = useRef<UserResponse | null>(null);
  const userScopeRef = useRef<string | null>(null);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityAtRef = useRef<number>(Date.now());
  const lastTokenRefreshAtRef = useRef<number>(0);

  const clearScopedDataCaches = useCallback(() => {
    clearDeviceMosquitoCache();
    clearHistoryCache();
    clearStatMosquitoCache();
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current !== null) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
  }, []);

  const resetAuthState = useCallback(() => {
    clearIdleTimer();

    if (userScopeRef.current !== null) {
      clearScopedDataCaches();
    }

    userScopeRef.current = null;
    userRef.current = null;
    setUser(null);
  }, [clearIdleTimer, clearScopedDataCaches]);

  const applyUserState = useCallback(
    (userData: UserResponse | null) => {
      const nextScopeKey = userData
        ? JSON.stringify({
            id: userData.id,
            regionId: userData.region_id ?? null,
            hasGlobalProjectVisibility:
              userData.has_global_project_visibility ?? null,
            managedProjectIds: [...(userData.managed_project_ids ?? [])].sort(
              (left, right) => left - right,
            ),
            visibleProjectIds: [...(userData.visible_project_ids ?? [])].sort(
              (left, right) => left - right,
            ),
          })
        : null;

      if (userScopeRef.current !== nextScopeKey) {
        clearScopedDataCaches();
      }

      userScopeRef.current = nextScopeKey;
      userRef.current = userData;
      setUser(userData);
    },
    [clearScopedDataCaches],
  );

  const markUnauthenticated = useCallback(
    (options?: { clearToken?: boolean; reason?: string }) => {
      if (options?.clearToken) {
        logout();
      }

      if (options?.reason) {
        sessionStorage.setItem(
          AUTH_REDIRECT_REASON_STORAGE_KEY,
          options.reason,
        );
      }

      resetAuthState();
      setAuthStatus("unauthenticated");
    },
    [resetAuthState],
  );

  const scheduleIdleTimeout = useCallback(() => {
    clearIdleTimer();

    if (!localStorage.getItem("accessToken")) {
      return;
    }

    const elapsedMs = Date.now() - lastActivityAtRef.current;
    const remainingMs = Math.max(IDLE_TIMEOUT_MS - elapsedMs, 0);

    idleTimeoutRef.current = setTimeout(() => {
      markUnauthenticated({
        clearToken: true,
        reason: "session-expired",
      });
    }, remainingMs);
  }, [clearIdleTimer, markUnauthenticated]);

  const isLatestRequest = useCallback(
    (requestId: number) =>
      isMountedRef.current && requestId === refreshRequestIdRef.current,
    [],
  );

  const isCurrentSessionRequest = useCallback(
    (requestId: number, tokenSnapshot: string | null) =>
      isLatestRequest(requestId) &&
      localStorage.getItem("accessToken") === tokenSnapshot,
    [isLatestRequest],
  );

  const runRefreshUser = useCallback(
    async (options?: { markLoading?: boolean }): Promise<RefreshUserResult> => {
      const requestId = ++refreshRequestIdRef.current;
      const tokenSnapshot = localStorage.getItem("accessToken");
      const markLoading = options?.markLoading ?? true;

      if (markLoading && isMountedRef.current) {
        setLoading(true);
      }

      if (!tokenSnapshot) {
        if (isCurrentSessionRequest(requestId, tokenSnapshot)) {
          markUnauthenticated();
        }

        return { user: null, status: "session-invalid" };
      }

      try {
        const userData = await getCurrentUser();

        if (!isCurrentSessionRequest(requestId, tokenSnapshot)) {
          return { user: userRef.current, status: "stale" };
        }

        if (!userData) {
          markUnauthenticated({
            clearToken: true,
            reason: "session-expired",
          });
          return { user: null, status: "session-invalid" };
        }

        applyUserState(userData);
        setAuthStatus("authenticated");
        scheduleIdleTimeout();

        return { user: userData, status: "success" };
      } catch (error) {
        console.error("获取用户信息失败", error);

        if (!isLatestRequest(requestId)) {
          return { user: userRef.current, status: "stale" };
        }

        const currentToken = localStorage.getItem("accessToken");
        if (!currentToken) {
          markUnauthenticated();
          return { user: null, status: "session-invalid" };
        }

        if (currentToken !== tokenSnapshot) {
          return { user: userRef.current, status: "stale" };
        }

        markUnauthenticated({
          clearToken: true,
          reason: "session-expired",
        });
        return { user: null, status: "session-invalid" };
      } finally {
        if (markLoading && isLatestRequest(requestId)) {
          setLoading(false);
        }
      }
    },
    [
      applyUserState,
      isCurrentSessionRequest,
      isLatestRequest,
      markUnauthenticated,
      scheduleIdleTimeout,
    ],
  );

  const refreshUser = useCallback(async (): Promise<UserResponse | null> => {
    const result = await runRefreshUser();
    return result.user;
  }, [runRefreshUser]);

  useEffect(() => {
    isMountedRef.current = true;
    void runRefreshUser({ markLoading: false });

    return () => {
      isMountedRef.current = false;
      refreshRequestIdRef.current += 1;
      clearIdleTimer();
    };
  }, [clearIdleTimer, runRefreshUser]);

  useEffect(() => {
    const refreshTokenForActivity = async (tokenSnapshot: string) => {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.error("刷新登录状态失败", error);

        if (localStorage.getItem("accessToken") === tokenSnapshot) {
          markUnauthenticated({
            clearToken: true,
            reason: "session-expired",
          });
        }
      }
    };

    const handleUserActivity = () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        return;
      }

      const now = Date.now();
      lastActivityAtRef.current = now;
      scheduleIdleTimeout();

      if (now - lastTokenRefreshAtRef.current < TOKEN_REFRESH_THROTTLE_MS) {
        return;
      }

      lastTokenRefreshAtRef.current = now;
      void refreshTokenForActivity(token);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleUserActivity();
      }
    };

    USER_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      USER_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [markUnauthenticated, scheduleIdleTimeout]);

  const handleLogin = async (account: string, password: string) => {
    try {
      const loginResult = await login(account, password);
      if (!loginResult) {
        markUnauthenticated({ clearToken: true });
        return false;
      }

      const refreshResult = await runRefreshUser();
      if (refreshResult.status === "success" && !!refreshResult.user) {
        lastActivityAtRef.current = Date.now();
        lastTokenRefreshAtRef.current = Date.now();
        scheduleIdleTimeout();
        return true;
      }

      return false;
    } catch (error) {
      console.error("登录失败", error);
      if (!localStorage.getItem("accessToken")) {
        markUnauthenticated({ clearToken: true });
      }
      throw error;
    }
  };

  const handleLogout = () => {
    markUnauthenticated({ clearToken: true });
  };

  const value: AuthContextType = {
    user,
    authStatus,
    loading,
    login: handleLogin,
    logout: handleLogout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
